import React, { ReactComponentElement } from 'react';
import useAsyncCallback from 'src/hooks/useAsyncCallback';
import {
    ButtonPrimary,
    DataTableBodyV2,
    DataTableCellV2,
    DataTableHeadCellV2,
    DataTableHeadV2,
    DataTableRowV2,
    DataTableV2,
    Heading,
    Select,
    SelectOption
} from '@americanexpress/dls-react';
import { FieldArray, Formik, FormikProps } from 'formik';
import { useAccountSyncStatuses, useVisibleAccounts } from 'src/hooks/queries/accounts';
import AdminService, { AdminSyncStatus, SetSyncStatusPayload } from 'src/services/admin.service';
import styles from './adminSetSyncStatusForm.module.scss';
import Selectors from 'src/utils/selectors';
import LiteralsService from 'src/services/literals/LiteralsService';

const SYNC_ERRORS: AdminSyncStatus[] = ['try_again_later', 'account_not_found', 'plaid_internal_error'];

export function AdminSetSyncStatusForm(): ReactComponentElement<any> {
    const { isLoading: isLoadingAccounts, data: accounts } = useVisibleAccounts();
    const { isLoading: isLoadingStatuses, data: statuses } = useAccountSyncStatuses();

    const handleSubmit = useAsyncCallback(async (values: SetSyncStatusPayload) => {
        await AdminService.setSyncStatus(values);
        location.reload();
    }, []);

    if (isLoadingAccounts || isLoadingStatuses) {
        return <p>{LiteralsService.getLiteral('Admin.Account.SyncStatus.loading')}</p>;
    } else {
        const initialValues: SetSyncStatusPayload = {
            accounts: accounts
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((account) => {
                    return { id: account.id, status: null };
                })
        };

        return (
            <div className={styles.container} data-cy={Selectors.Admin.ADMIN_SYNC_STATUS_FORM}>
                <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
                    {(props: FormikProps<SetSyncStatusPayload>) => (
                        <form
                            onSubmit={props.handleSubmit}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '20px'
                            }}
                        >
                            <div>
                                <Heading level={1}>
                                    {LiteralsService.getLiteral('Admin.Account.SyncStatus.title')}
                                </Heading>
                                <FieldArray
                                    name="accounts"
                                    render={({ replace }) => (
                                        <DataTableV2 bordered small id="account-sync-errors-table">
                                            <DataTableHeadV2>
                                                <DataTableRowV2>
                                                    <DataTableHeadCellV2 align="left">
                                                        {LiteralsService.getLiteral(
                                                            'Admin.Account.SyncStatus.accountHeader'
                                                        )}
                                                    </DataTableHeadCellV2>
                                                    <DataTableHeadCellV2 align="left">
                                                        {LiteralsService.getLiteral(
                                                            'Admin.Account.SyncStatus.currentStatusHeader'
                                                        )}
                                                    </DataTableHeadCellV2>
                                                    <DataTableHeadCellV2 align="left">
                                                        {LiteralsService.getLiteral(
                                                            'Admin.Account.SyncStatus.newStatusHeader'
                                                        )}
                                                    </DataTableHeadCellV2>
                                                </DataTableRowV2>
                                            </DataTableHeadV2>
                                            <DataTableBodyV2>
                                                {accounts.map((account, index) => {
                                                    const status = statuses.find(
                                                        ({ accountId }) => account.accountId === accountId
                                                    );
                                                    const statusString = `${status?.syncResult} ${status?.error}`;
                                                    return (
                                                        <DataTableRowV2
                                                            key={index}
                                                            data-cy={Selectors.Admin.SYNC_ERROR_ACCOUNT}
                                                        >
                                                            <DataTableCellV2>{account.name}</DataTableCellV2>
                                                            <DataTableCellV2>{statusString}</DataTableCellV2>
                                                            <DataTableCellV2>
                                                                <Select
                                                                    id={`accounts.${index}`}
                                                                    value={props.values.accounts[index]?.status || ''}
                                                                    data-cy={Selectors.Admin.ERROR_STATUS_SELECTOR}
                                                                    onChange={(e: any) => {
                                                                        replace(index, {
                                                                            id: account.id,
                                                                            status: e.target.value
                                                                        });
                                                                    }}
                                                                >
                                                                    <SelectOption value={null}>
                                                                        {LiteralsService.getLiteral(
                                                                            'Admin.Account.SyncStatus.unchanged'
                                                                        )}
                                                                    </SelectOption>
                                                                    {SYNC_ERRORS.map((error) => (
                                                                        <SelectOption value={error} key={error}>
                                                                            {error}
                                                                        </SelectOption>
                                                                    ))}
                                                                </Select>
                                                            </DataTableCellV2>
                                                        </DataTableRowV2>
                                                    );
                                                })}
                                            </DataTableBodyV2>
                                        </DataTableV2>
                                    )}
                                />
                            </div>

                            <ButtonPrimary type="submit" data-cy={Selectors.Admin.SAVE_ACCOUNT_ERRORS_BUTTON}>
                                {LiteralsService.getLiteral('Admin.Account.SyncStatus.saveButton')}
                            </ButtonPrimary>
                        </form>
                    )}
                </Formik>
            </div>
        );
    }
}
